import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, TextField, Button, Typography, Alert, ThemeProvider, createTheme } from '@mui/material';
import { apiPost } from '../utils/api';
import ChangePasswordDialog from './ChangePasswordDialog';

const theme = createTheme({
  palette: {
    primary: {
      main: '#94BA33',
    },
  },
});

function Login({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiPost('/api/login', credentials);
      
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        
        // Check if user must change password
        if (response.data.mustChangePassword) {
          setTempUserData(response.data.user);
          setShowChangePassword(true);
        } else {
          onLogin(response.data.user);
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
      // Log error in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = () => {
    // Clear token and redirect to login
    localStorage.removeItem('token');
    setShowChangePassword(false);
    setTempUserData(null);
    setCredentials({ username: '', password: '' });
    setError('');
    setSuccess('Password changed successfully! Please log in with your new password.');
  };

  return (
    <ThemeProvider theme={theme}>
      <ChangePasswordDialog 
        open={showChangePassword} 
        onPasswordChanged={handlePasswordChanged}
      />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Paper sx={{ p: 4, width: 400 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box sx={{ 
              backgroundColor: 'white', 
              borderRadius: 1, 
              p: 1,
              display: 'flex',
              alignItems: 'center',
              boxShadow: 1
            }}>
              <img 
                src="/thumbnail_image.png" 
                alt="Evoke Medical Care" 
                style={{ height: 48, display: 'block' }}
              />
            </Box>
          </Box>
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#94BA33', fontWeight: 600 }}>
            Account Onboarding Portal
          </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={credentials.username}
            onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            margin="normal"
            required
          />
          <Button
            type="submit"
            fullWidth
            variant="outlined"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

Login.propTypes = {
  onLogin: PropTypes.func.isRequired,
};

export default Login;